// Unit conversion utilities for metric and imperial systems
import { USER_STORAGE_KEY } from '@/lib/api/auth';

export type MeasurementSystem = 'metric' | 'imperial';
const KG_PER_LB = 0.45359237;
const LBS_PER_KG = 1 / KG_PER_LB;

// Get measurement system from localStorage
export const getMeasurementSystem = (): MeasurementSystem => {
  try {
    const userJson = localStorage.getItem(USER_STORAGE_KEY);
    if (userJson) {
      const user = JSON.parse(userJson);
      if (user.preferredMeasurementSystem === 'imperial') {
        return 'imperial';
      }
    }
  } catch (error) {
    // Silent fail, default to metric
    console.warn('Error reading measurement system preference:', error);
  }
  return 'metric';
};

// Get weight unit label
export const getWeightUnit = (system: MeasurementSystem): string => {
  return system === 'imperial' ? 'lbs' : 'kg';
};

// Get height unit label
export const getHeightUnit = (system: MeasurementSystem): string => {
  return system === 'imperial' ? 'ft/in' : 'cm';
};

// Convert weight from lbs to kg (for API when nutritionist uses imperial)
export const lbsToKg = (lbs: number): number => {
  return lbs * KG_PER_LB;
};

// Convert weight from kg to lbs (for display when nutritionist uses imperial)
export const kgToLbs = (kg: number): number => {
  const n = Number(kg);
  return isNaN(n) ? 0 : n * LBS_PER_KG;
};

// Format weight for display
export const formatWeight = (kg: number, system: MeasurementSystem): string => {
  const kgNum = Number(kg);
  if (Number.isNaN(kgNum)) return '';
  if (system === 'imperial') {
    return `${kgToLbs(kgNum).toFixed(1)} lbs`;
  }
  return `${kgNum.toFixed(1)} kg`;
};

// Format height for display
export const formatHeight = (cm: number, system: MeasurementSystem): string => {
  const cmNum = Number(cm);
  if (Number.isNaN(cmNum)) return '';
  if (system === 'imperial') {
    const totalInches = cmNum / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}' ${inches}"`;
  }
  return `${cmNum.toFixed(0)} cm`;
};
